import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { estaEn, useCambiosSucursal, usePersonalActivo, type Sucursal } from './datos';

// «Sucursales - Asignar personal» del Figma: la lista del personal activo, con
// una casilla por persona, y cuántas van marcadas.
//
// ## Lo que se guarda son los cambios, no la lista
//
// La pantalla no guarda «la lista de esta sucursal»: la API no tiene ese
// recurso, porque las sucursales de alguien son parte de su ficha (el agregado
// `Staff`). Así que acá se lleva solo lo que la persona **tocó**, encima de lo que
// vino del servidor, y al guardar viaja una petición por cada una que cambió.
// Quien no se tocó no se manda, y su versión no se arriesga a chocar con nadie.
//
// El personal inactivo no aparece: no se le asignan sucursales a quien no está
// trabajando, y lo que ya tenía no se toca porque no se manda.

interface Props {
  sucursal: Sucursal | null;
  onCerrar: () => void;
}

export function AsignarPersonal({ sucursal, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosSucursal();
  const personal = usePersonalActivo(sucursal !== null);

  /** Lo que se tocó: id de la persona → si quedó marcada. */
  const [tocadas, setTocadas] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [guardando, setGuardando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  const idSucursal = sucursal ? String(sucursal.id) : '';
  const lista = personal.data ?? [];
  const marcada = (id: string, deServidor: boolean) => tocadas.get(id) ?? deServidor;
  const cuantas = lista.filter((p) => marcada(String(p.id), estaEn(p, idSucursal))).length;

  function cerrar() {
    if (guardando) return;
    setTocadas(new Map());
    setProblema(null);
    onCerrar();
  }

  async function guardar() {
    if (!sucursal) return;
    const marcadas = new Set(
      lista.filter((p) => marcada(String(p.id), estaEn(p, idSucursal))).map((p) => String(p.id)),
    );

    setGuardando(true);
    setProblema(null);
    try {
      const error = await cambios.asignarPersonal(idSucursal, lista, marcadas);
      if (error) {
        // Lo que alcanzó a guardarse quedó guardado; se descartan los toques para
        // que la lista muestre lo que de verdad hay ahora.
        setTocadas(new Map());
        setProblema(error.code ? traducirError(error.code, t) : t('ui.formulario.aviso.sinRespuesta'));
        return;
      }
    } catch {
      // Sin red la petición lanza; sin esto «Cancelar» quedaba apagado para siempre (#124).
      setTocadas(new Map());
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
      return;
    } finally {
      setGuardando(false);
    }
    avisar({ titulo: t('admin.sucursales.aviso.personal', { nombre: sucursal.name }) });
    setTocadas(new Map());
    onCerrar();
  }

  return (
    <Ventana
      abierta={sucursal !== null}
      ancho={520}
      titulo={sucursal ? t('admin.sucursales.asignar.titulo', { nombre: sucursal.name }) : ''}
      info={t('admin.sucursales.asignar.texto')}
      izquierda={personal.isSuccess && lista.length > 0 && t('admin.sucursales.asignar.seleccionadas', { n: cuantas })}
      acciones={
        <>
          <Boton variante="texto" disabled={guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton
            // Mientras se vuelve a pedir, la lista en pantalla puede ser la vieja:
            // guardar con esas versiones chocaría. Se espera a la fresca.
            disabled={guardando || personal.isFetching || !personal.isSuccess || lista.length === 0}
            aria-busy={guardando}
            onClick={() => void guardar()}
          >
            {guardando ? t('ui.formulario.guardando') : t('app.guardar')}
          </Boton>
        </>
      }
    >
      {problema && <AvisoError aviso={{ titulo: t('admin.sucursales.error.titulo'), texto: problema }} />}

      {personal.isPending && <p className="ventana-nota">{t('app.cargando')}</p>}
      {personal.isError && <p className="ventana-nota">{t('admin.sucursales.asignar.error')}</p>}
      {personal.isSuccess && lista.length === 0 && (
        <p className="ventana-nota">{t('admin.sucursales.asignar.sinPersonal')}</p>
      )}

      {lista.length > 0 && (
        <ul className="lista-personal">
          {lista.map((p) => {
            const id = String(p.id);
            const detalle = [p.employeeCode, p.specialties.join(', ')].filter(Boolean).join(' · ');
            return (
              <li key={id}>
                <label>
                  <input
                    type="checkbox"
                    checked={marcada(id, estaEn(p, idSucursal))}
                    disabled={guardando}
                    onChange={(e) => setTocadas((m) => new Map(m).set(id, e.target.checked))}
                  />
                  <span>
                    <strong>
                      {p.firstName} {p.lastName}
                    </strong>
                    <small>{detalle}</small>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </Ventana>
  );
}
